import { CheckCircle, XCircle, Loader2, Download } from "lucide-react";
import { ImportResult } from "../../../services/web-scraper.service";

interface ImportConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemCount: number;
  storeName: string;
  isImporting: boolean;
  importResult: ImportResult | null;
}

export default function ImportConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  itemCount,
  storeName,
  isImporting,
  importResult,
}: ImportConfirmationDialogProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50"
      onClick={!isImporting ? onClose : undefined}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {importResult ? (
          <>
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Import Complete</h2>
            </div>
            <div className="space-y-2 mb-6">
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-lg px-4 py-2">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span>{importResult.imported} file{importResult.imported !== 1 ? "s" : ""} imported successfully</span>
              </div>
              {importResult.failed > 0 && (
                <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 rounded-lg px-4 py-2">
                  <XCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{importResult.failed} file{importResult.failed !== 1 ? "s" : ""} failed — check items list filtered by status: Failed</span>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
                type="button"
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Download className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">
                Import {itemCount} file{itemCount !== 1 ? "s" : ""} into "{storeName}"?
              </h2>
              <p className="text-sm text-gray-500 mt-2">
                This will download and add the selected files to your Document Store.
                Files already imported will be skipped automatically.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                disabled={isImporting}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isImporting}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                type="button"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Confirm Import
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
